<template>
  <Grid v-if="pageData">
    <Heading :level="1" :content="pageData.title" class="sr-only" />
    <Heading :level="2" class="sr-only" content="Uitgelichte artikelen" />
    <TeaserGroupSpotlightGrid
      v-if="state.featured"
      class="is--full-width-small"
      :articles="state.featured"
      :spotlight="true"
      priority="high"
      :padded="false"
      :show-dates="true"
      :analytics="{ linkAnalyticsTag: 'spotlight' }"
    />

    <SectionDossiers
      v-if="state.dossiers && state.dossiers.length > 0"
      :items="state.dossiers"
      :show-button="false"
      :_index="1"
    />

    <TeaserGroupLoadMoreGrid
      v-if="state.related && state.related.length >= 1"
      :title="`Meer ${pageData.title}`"
      :initial-items="[...state.related]"
      loading="lazy"
      :heading-level="3"
      :offset="(state.featured?.length || 0) + state.related.length"
      :filter="
        useRelatedFilter(
          {
            field: 'themes',
            id: pageData.id,
          },
          false,
        )
      "
      :teaser-count="12"
      :cache-key="baseCacheKey"
    />
  </Grid>
</template>

<script setup lang="ts">
import usePageStore from '~/stores/page';
import useRelatedFilter from '~/composables/useRelatedFilter';
import useCollectionInstance from '~/composables/useCollectionInstance';
import type { ICmsThemes } from '~~/types/_cms-types';
import useTeasers from '~/composables/useTeasers';
import type { ICollectionTeaser, ITeaser } from '~~/types/local_types';
import useAdapter from '~/composables/useAdapter';
import useCheckRedirect from '~/composables/useCheckRedirect';

const pageStore = usePageStore();
const { getTeasers } = useTeasers();
const { dossierTeaserAdapter } = useAdapter();
const { slug } = useRoute().params;
const baseCacheKey = `themes-${slug}`;

const pageData = (await useCollectionInstance('themes')) as ICmsThemes;

if (!pageData) {
  throw createError({ statusCode: 404, statusMessage: 'Thema niet gevonden' });
}

usePageSeo({
  pageTitle: pageData.title,
  pageDescription: `Lees hier het laatste nieuws, achtergronden en analyses over ${pageData.title}. Blijf op de hoogte van de belangrijkste ontwikkelingen en verhalen binnen dit onderwerp.`,
  section: "Thema's",
});

if (import.meta.client) {
  sendAnalytics({ prefix: 'themas', slug: pageData.slug });
}

await useCheckRedirect(pageData.slug, 'themas');

const themeFilter = useRelatedFilter({ field: 'themes', id: pageData.id }, false);

const dossiersFilter = {
  themes: { themes_id: pageData.id },
};

interface IState {
  dossiers?: ICollectionTeaser[];
  featured?: ITeaser[];
  related?: ITeaser[];
}

const state = reactive<IState>({
  dossiers: undefined,
  featured: undefined,
  related: undefined,
});

const itemCount = 7 + 12;
const items = await getTeasers(
  itemCount,
  themeFilter as never,
  `${baseCacheKey}-featured`,
  '-publish_time',
);
state.featured = items?.slice(0, 7);
state.related = items?.slice(7);

const data = await getTeasers(
  -1,
  dossiersFilter as never,
  `${baseCacheKey}-dossiers`,
  '-adjust_time',
  0,
  false,
  'dossiers',
);
state.dossiers = data?.map(dossierTeaserAdapter) as ICollectionTeaser[];

pageStore.setCurrentPage(undefined);
</script>

<style scoped lang="postcss">
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}
</style>
